import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getBulkSCGJob, cancelBulkSCGJob, getBulkSCGRejectedRules } from '../../services/api';
import BulkSCGFileRow from './BulkSCGFileRow';
import RejectedRulesReview from '../RejectedRulesReview';

const TERMINAL = new Set(['completed', 'completed_with_errors', 'failed', 'cancelled']);

const STATUS_META = {
  queued:                 { label: 'Queued',                  bg: 'bg-gray-200',    text: 'text-gray-800' },
  running:                { label: 'Running',                 bg: 'bg-indigo-100',  text: 'text-indigo-800' },
  completed:              { label: 'Completed',               bg: 'bg-emerald-100', text: 'text-emerald-800' },
  completed_with_errors:  { label: 'Completed (with errors)', bg: 'bg-amber-100',   text: 'text-amber-800' },
  failed:                 { label: 'Failed',                  bg: 'bg-red-100',     text: 'text-red-800' },
  cancelled:              { label: 'Cancelled',               bg: 'bg-gray-200',    text: 'text-gray-600' },
};

const formatTime = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return '—';
  }
};

export default function BulkSCGJobCard({ batchId, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filePage, setFilePage] = useState(0);
  const [showRejected, setShowRejected] = useState(false);
  const queryClient = useQueryClient();

  const { data: job } = useQuery({
    queryKey: ['bulk-scg-job', batchId],
    queryFn: () => getBulkSCGJob(batchId),
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      return status && TERMINAL.has(status) ? false : 2000;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelBulkSCGJob(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-scg-job', batchId] });
      queryClient.invalidateQueries({ queryKey: ['bulk-scg-jobs'] });
    },
  });

  // Rejected rules are fetched only when the user opens the review panel.
  const { data: rejectedData, isFetching: rejectedLoading } = useQuery({
    queryKey: ['bulk-scg-rejected', batchId],
    queryFn: () => getBulkSCGRejectedRules(batchId),
    enabled: showRejected,
    staleTime: 60 * 1000,
  });

  if (!job) {
    return (
      <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg p-4">
        <div className="text-sm text-[var(--text-secondary)]">Loading batch {batchId}…</div>
      </div>
    );
  }

  const totals = job.totals || {};
  const filesTotal = totals.files_total || (job.files || []).length || 0;
  const done = (totals.files_completed || 0) + (totals.files_failed || 0) + (totals.files_cancelled || 0);
  const pct = filesTotal > 0 ? Math.min(100, Math.round((done * 100) / filesTotal)) : 0;
  const meta = STATUS_META[job.status] || STATUS_META.queued;
  const isActive = !TERMINAL.has(job.status);

  const allFiles = job.files || [];
  const PAGE_SIZE = 50;
  const pageCount = Math.max(1, Math.ceil(allFiles.length / PAGE_SIZE));
  const filesShown = allFiles.slice(filePage * PAGE_SIZE, (filePage + 1) * PAGE_SIZE);

  // Progress-bar fill picks an accent that signals state at a glance.
  const barFill =
    job.status === 'failed'
      ? 'bg-[var(--error)]'
      : job.status === 'completed_with_errors'
      ? 'bg-[var(--warning)]'
      : job.status === 'cancelled'
      ? 'bg-[var(--vault-border-light)]'
      : job.status === 'completed'
      ? 'bg-[var(--success)]'
      : 'bg-[var(--spotlight-gold)]';

  return (
    <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg overflow-hidden">
      <div className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
          </button>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${meta.bg} ${meta.text}`}
          >
            {meta.label}
          </span>
          <span className="text-sm font-mono text-[var(--text-secondary)]">{job.batch_id}</span>
          {job.source_kind === 'zip' && (
            <span className="text-xs text-[var(--text-muted)] italic">zip</span>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs text-[var(--text-muted)]">
            <span>created {formatTime(job.created_at)}</span>
            {isActive && (
              <button
                type="button"
                disabled={cancelMutation.isLoading}
                onClick={() => cancelMutation.mutate()}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[var(--vault-border)] text-[var(--text-secondary)] hover:border-[var(--spotlight-gold)] hover:text-[var(--text-primary)] disabled:opacity-50 transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-[var(--vault-bg)] border border-[var(--vault-border)] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${barFill}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
            {done}/{filesTotal} files · {totals.rules_indexed || 0} rules
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-muted)]">
          {totals.files_completed > 0 && (
            <span className="text-[var(--text-secondary)]">{totals.files_completed} completed</span>
          )}
          {totals.files_failed > 0 && (
            <span className="text-[#e57373]">{totals.files_failed} failed</span>
          )}
          {totals.files_cancelled > 0 && <span>{totals.files_cancelled} cancelled</span>}
          {totals.rules_rejected > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowRejected(true); }}
              className="text-amber-700 hover:text-amber-900 hover:underline font-medium"
              title="Rules the extraction validator routed to review (portion-marking conflicts, unrecognized levels, non-rule content)"
            >
              {rejectedLoading ? 'Loading rejected rules…' : `Review ${totals.rules_rejected} rejected rule${totals.rules_rejected === 1 ? '' : 's'}`}
            </button>
          )}
          {job.completed_at && <span>finished {formatTime(job.completed_at)}</span>}
        </div>
      </div>

      {showRejected && rejectedData?.rejected_rules?.length > 0 && (
        <RejectedRulesReview
          rejectedRules={rejectedData.rejected_rules}
          sourceFile={`batch ${batchId}`}
          onClose={() => setShowRejected(false)}
        />
      )}

      {expanded && allFiles.length > 0 && (
        <div className="border-t border-[var(--vault-border)] bg-[var(--vault-bg)]">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[var(--text-muted)] bg-[var(--vault-surface)]">
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium text-right">Rules</th>
                <th className="px-3 py-2 font-medium">Attempts</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {filesShown.map((f) => (
                <BulkSCGFileRow key={f.idx ?? f.filename} file={f} />
              ))}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="px-3 py-2 flex items-center justify-between text-xs text-[var(--text-secondary)] border-t border-[var(--vault-border)]">
              <span>
                Showing {filePage * PAGE_SIZE + 1}–{Math.min((filePage + 1) * PAGE_SIZE, allFiles.length)} of {allFiles.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={filePage === 0}
                  onClick={() => setFilePage((p) => Math.max(0, p - 1))}
                  className="px-2 py-1 rounded border border-[var(--vault-border)] text-[var(--text-secondary)] hover:border-[var(--spotlight-gold)] disabled:opacity-40 transition-colors"
                >
                  Prev
                </button>
                <span>
                  page {filePage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={filePage >= pageCount - 1}
                  onClick={() => setFilePage((p) => Math.min(pageCount - 1, p + 1))}
                  className="px-2 py-1 rounded border border-[var(--vault-border)] text-[var(--text-secondary)] hover:border-[var(--spotlight-gold)] disabled:opacity-40 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
